import { FC, MouseEvent, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { css, Theme, useTheme } from '@emotion/react';
import { CaretDown, User } from '@phosphor-icons/react';
import { AccountUserRole } from 'common/CommonConstants';
import { getPlaceholderUrl } from 'common/commonUtil';
import { useUser } from 'client/lib/User';
import { Button } from 'client/components/Button';
import { SearchableDropdown, SearchableDropdownRenderOptionFunction } from 'client/components/Dropdown/SearchableDropdown';
import { adminSubtitle, memberSubtitle } from 'client/components/Other/AccountSelect';
import { ListItemFigure } from 'client/components/Other/ListItemFigure';
import { ListItemText } from 'client/components/Other/ListItemText';
import { borderRadius } from 'client/Theme';

interface AccountOption {
  id: string;
  accountID: number;
  name: string;
  accountUserRoleID: AccountUserRole;
}

const renderAccountOption: SearchableDropdownRenderOptionFunction<AccountOption> = (props, option, { selected }) => {
  const theme = useTheme();
  return (
    <li {...props} key={option.id} css={optionStyle}>
      <ListItemFigure image={{ alt: 'account profile picture', src: getPlaceholderUrl('account', option.accountID) }} size={24} />
      <ListItemText
        primary={option.name}
        secondary={option.accountUserRoleID === AccountUserRole.ADMINISTRATOR ? adminSubtitle(theme) : memberSubtitle}
      />
    </li>
  );
};

export const AccountSwitcher: FC = () => {
  const theme = useTheme();
  const { user, account: lastAccountIdentifier } = useUser();
  const { account: identifier } = useParams();
  const navigate = useNavigate();
  const [buttonEl, setButtonEl] = useState<HTMLElement | null>(null);

  const account = user?.accounts.find((account) => account.identifier === (identifier || lastAccountIdentifier));

  const accountOptions = useMemo<AccountOption[]>(
    () =>
      (user?.accounts || []).map((account) => ({
        id: account.identifier,
        accountID: account.id,
        name: account.name,
        accountUserRoleID: account.accountUserRoleID,
      })),
    [user?.accounts]
  );

  const handleAccountClick = useCallback((event: MouseEvent<HTMLButtonElement>) => setButtonEl(event.currentTarget), []);

  const handleAccountClose = useCallback(() => setButtonEl(null), []);

  const handleAccountChange = useCallback((option: AccountOption | null) => option && navigate(`/${option.id}/projects`), []);

  return (
    <>
      <Button icon={<CaretDown size={12} />} tooltip="Switch account" tooltipSide="right" onClick={handleAccountClick} css={buttonStyle}>
        {!account && (
          <>
            <ListItemFigure icon={<User size={16} weight="bold" color={theme.color.grey[80]} />} size={24} />
            <span>Choose an account…</span>
          </>
        )}
        {!!account && (
          <>
            <ListItemFigure image={{ alt: 'account profile picture', src: getPlaceholderUrl('account', account.id) }} size={24} />
            <span>{account.name}</span>
          </>
        )}
      </Button>

      <SearchableDropdown
        anchorEl={buttonEl}
        value={account?.identifier || null}
        options={accountOptions}
        getOptionName={(option) => option.name}
        renderOption={renderAccountOption}
        popoverProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        }}
        onClose={handleAccountClose}
        onChange={handleAccountChange}
      />
    </>
  );
};

const buttonStyle = (theme: Theme) => css`
  background-color: transparent;
  border-radius: ${borderRadius}px;

  .icon {
    margin-left: 0px !important;
    transform: translateY(1px);
  }

  figure {
    border-radius: ${borderRadius}px;
    background-color: ${theme.color.grey[60]};

    > svg {
      margin: 4px;
    }
  }

  > span {
    display: flex;
    gap: 8px;
    margin: 0 8px;

    > span {
      transform: translateY(1px);
    }
  }
`;

const optionStyle = css`
  gap: 8px;
  border-radius: ${borderRadius}px;
  overflow: hidden;

  > svg {
    margin-right: 8px;
  }

  > .MuiListItemText-root {
    > .MuiListItemText-primary {
      font-weight: bold;
    }

    > .MuiListItemText-secondary {
      display: flex;
      align-items: center;

      > .tag {
        margin-right: 6px;
        cursor: pointer;
      }

      > svg {
        margin-right: 2px;
      }
    }
  }
`;
